// client/src/utils/subjectColours.generated.js
//
// GENERATED - do not edit by hand.
//
// Copied from packages/core/src/theme/subjectColours.js by tools/sync-shared.py. Edit the core file and re-run that
// script; `node tools/marks-sheet-conformance.mjs` asserts this copy has not
// drifted from it.
//
// The phone imports the original. The web cannot (packages/core is outside
// client/src, and the CRA build will not resolve it), so it gets this. They must
// stay identical - a rule that exists twice and differs is the whole class of
// bug these files were written to end.
//
// What colour a subject is on the timetable. Hue by FAMILY, not by
// subject: no categorical palette holds thirty separable hues. The
// values were run through the dataviz validator in both modes -- green
// was dropped because under deuteranopia it sat 4.6 from red.

const PATTERNS = [
  ['technical', /\b(ict|comput|informatic|technical\s*draw|engineer|electric|electronic|mechanic|technolog)/i],
  ['practical', /\b(physical\s*ed|sport|music|art|draw|paint|home\s*econom|agric|craft|creativ|food|nutrition|needle|handicraft)/i],
  ['commercial', /\b(econom|account|commerce|business|management|finance|market)/i],
  ['humanities', /\b(history|geograph|citizen|civic|philosoph|religio|moral|environment|social\s*stud)/i],
  ['language', /\b(english|french|literature|german|spanish|latin|lang|linguistic)/i],
  ['science', /\b(maths?|mathematic|further\s*math|add(itional)?\s*math|physic(?!al)|chem|bio|science|stat)/i],
];

export function familyForSubject(name, code) {
  const text = `${String(name || '')} ${String(code || '')}`.trim();
  if (!text) return UNCLASSIFIED;
  for (const [id, pattern] of PATTERNS) {
    if (pattern.test(text)) return SUBJECT_FAMILIES.find((f) => f.id === id) || UNCLASSIFIED;
  }
  return UNCLASSIFIED;
}

/**
 * The colours one subject's chip is drawn in.
 *
 * `override` is `subjects.colorHex` — a school that wants Chemistry green gets
 * Chemistry green, and it is stored so a printed chart stays valid. The
 * override sets the rule only; the ground stays a pale tint so the chip
 * survives a photocopier whatever colour the school picked.
 */
export function subjectChipColours({ name, code, colorHex, dark = false } = {}) {
  const family = familyForSubject(name, code);
  const rule = /^#[0-9a-fA-F]{6}$/.test(String(colorHex || '')) ? colorHex : null;
  return {
    familyId: family.id,
    familyLabel: family.label,
    rule: rule || (dark ? family.darkRule : family.rule),
    background: dark ? family.darkTint : family.tint,
    ink: dark ? family.darkInk : family.ink,
  };
}

/** The families actually present in a set of subjects, for a legend. */
export function legendFor(subjects = [], dark = false) {
  const seen = new Map();
  for (const s of subjects) {
    const family = familyForSubject(s?.name ?? s?.subjectName, s?.code ?? s?.subjectCode);
    if (!seen.has(family.id)) {
      seen.set(family.id, {
        id: family.id,
        label: family.label,
        rule: dark ? family.darkRule : family.rule,
        background: dark ? family.darkTint : family.tint,
      });
    }
  }
  // Fixed order, so the legend does not rearrange itself between classes.
  const order = [...SUBJECT_FAMILIES.map((f) => f.id), UNCLASSIFIED.id];
  return [...seen.values()].sort((a, b) => order.indexOf(a.id) - order.indexOf(b.id));
}

/**
 * The six families, in the order the validator checked them.
 *
 * `rule` is the saturated hue — the chip's left rule and its legend swatch.
 * `tint`/`ink` are the chip's ground and text in light mode, `darkTint`/
 * `darkInk` in dark. The saturated hue is never a large fill.
 */
export const SUBJECT_FAMILIES = [
  {
    id: 'science', label: 'Sciences',
    rule: '#2E6FBF', tint: '#E7F1FB', ink: '#173A63',
    darkRule: '#4E8FD8', darkTint: '#16283F', darkInk: '#CFE1F5',
  },
  {
    id: 'language', label: 'Languages',
    rule: '#C1521A', tint: '#FBEDE3', ink: '#6B2D0D',
    darkRule: '#C97A3A', darkTint: '#3A2313', darkInk: '#F6DCC8',
  },
  {
    id: 'technical', label: 'Technical & ICT',
    rule: '#0E86A8', tint: '#E1F2F7', ink: '#0A4A5D',
    darkRule: '#189BBC', darkTint: '#0F2E37', darkInk: '#C9E8F1',
  },
  {
    id: 'humanities', label: 'Humanities',
    rule: '#C42A2A', tint: '#FBE8E8', ink: '#711818',
    darkRule: '#DB5F5F', darkTint: '#3B1919', darkInk: '#F7D4D4',
  },
  {
    id: 'commercial', label: 'Commercial',
    rule: '#9B34B8', tint: '#F4E7F8', ink: '#5A1D6B',
    darkRule: '#AE6BCC', darkTint: '#2E1837', darkInk: '#EBD3F4',
  },
  {
    id: 'practical', label: 'Practical & Arts',
    rule: '#9A7A0A', tint: '#F7F1DC', ink: '#584600',
    darkRule: '#A38C2C', darkTint: '#332C10', darkInk: '#EFE4BC',
  },
];

/**
 * The family for a subject with no recognisable name.
 *
 * NEUTRAL, NOT A SEVENTH HUE. An unclassified subject is the absence of a
 * family, and giving it a colour of its own would imply it belongs to a group
 * that does not exist — and would push the palette past the point where the
 * hues stay separable.
 */
export const UNCLASSIFIED = {
  id: 'other', label: 'Other',
  rule: '#6B7280', tint: '#F1F2F4', ink: '#374151',
  darkRule: '#9AA3AF', darkTint: '#24272D', darkInk: '#D9DDE3',
};
